import { Fragment } from 'react';
import { StyleSheet, View } from 'react-native';

import type { PageBuilderFooter, PageBuilderIcon } from '@fhs/cms-groq-queries';
import { Text, XStack, YStack, createMqStyles, tokens, useMqSelect } from '@fhs/ui';

import { ActionLink } from '../components/action-link';
import { BrightEdgeContainer } from '../components/brightedge-container';
import { Icon } from '../components/icon';
import { SimpleBlockContent } from '../components/simple-block-content';
import type { ActionHandler } from '../types';
import { addActionSource } from '../utils/action';

export type LayoutFooterProps = Pick<
  PageBuilderFooter,
  'legalLinks' | 'legalText' | 'linkGroups' | 'socialLinks'
> & {
  onAction: ActionHandler;
};

export function LayoutFooter(props: LayoutFooterProps) {
  const mqStyles = useMqStyles();
  const iconSize = useMqSelect({ $gteDesktop: 24 }, 20);

  return (
    <YStack style={mqStyles.footerContainer}>
      <YStack style={mqStyles.footerContentArea}>
        <View style={mqStyles.aboveDividerContentArea}>
          <View style={mqStyles.linkGroupArea}>
            {props.linkGroups.map(linkGroup => {
              return (
                <YStack style={mqStyles.linkGroup} key={linkGroup._key}>
                  {Boolean(linkGroup.label) && (
                    <Text.Heading type="four" role="heading" aria-level={2}>
                      {linkGroup.label}
                    </Text.Heading>
                  )}
                  {(linkGroup.textLinks ?? []).map(textLink => {
                    return (
                      <ActionLink
                        key={textLink._key}
                        onAction={addActionSource(
                          { component: 'text-link', componentType: 'link' },
                          props.onAction
                        )}
                        action={textLink.link}
                      >
                        <Text.Paragraph size="sm">{textLink.text}</Text.Paragraph>
                      </ActionLink>
                    );
                  })}
                </YStack>
              );
            })}
          </View>
          <XStack style={mqStyles.socialLinkArea}>
            {(props.socialLinks ?? []).map(socialLink => {
              const socialLabel = getIconLabel(socialLink.icon);

              return (
                <ActionLink
                  key={socialLink._key}
                  action={socialLink.link}
                  onAction={addActionSource(
                    { component: 'social-link', componentType: 'link' },
                    props.onAction
                  )}
                  accessibilityLabel={socialLabel}
                >
                  <Icon size={iconSize} color={tokens.colors.$houseDark} name={socialLink.icon} aria-hidden />
                </ActionLink>
              );
            })}
          </XStack>
        </View>
        <HorizontalDivider />
        <BrightEdgeContainer divider={<HorizontalDivider />} />
        <View style={mqStyles.belowDividerContentArea}>
          <XStack>
            {props.legalText && (
              <SimpleBlockContent
                onAction={addActionSource(
                  { component: 'legal-text', componentType: 'block' },
                  props.onAction
                )}
                value={props.legalText}
                normalText={{ style: mqStyles.legalText, size: 'sm' }}
                linkText={{ style: mqStyles.legalTextLink }}
              />
            )}
          </XStack>
          <XStack style={mqStyles.legalLinksArea}>
            {(props.legalLinks ?? []).map((legalLink, idx) => {
              const isLast = idx === props.legalLinks.length - 1;

              return (
                <Fragment key={legalLink._key}>
                  <ActionLink
                    action={legalLink.link}
                    onAction={addActionSource(
                      { component: 'legal-link', componentType: 'link' },
                      props.onAction
                    )}
                    accessibilityLabel={
                      'icon' in legalLink ? getIconLabel(legalLink.icon) : undefined
                    }
                  >
                    {'text' in legalLink && (
                      <Text.Paragraph size="sm" style={mqStyles.legalLink}>
                        {legalLink.text}
                      </Text.Paragraph>
                    )}
                    {'icon' in legalLink && (
                      <Icon color={tokens.colors.$blackOpacity75} name={legalLink.icon} />
                    )}
                  </ActionLink>
                  {!isLast && <VerticalDivider />}
                </Fragment>
              );
            })}
          </XStack>
        </View>
      </YStack>
    </YStack>
  );
}

const useMqStyles = createMqStyles({
  footerContainer: {
    $base: {
      borderTopWidth: 1,
      borderTopColor: tokens.colors.$blackOpacity10,
      backgroundColor: tokens.colors.$houseLight,
      paddingTop: 56,
      paddingBottom: 40,
      marginTop: 24,
    },
    $gteDesktop: {
      marginTop: 32,
    },
  },
  footerContentArea: {
    $base: {
      width: '100%',
      maxWidth: 1400,
      marginHorizontal: 'auto',
      paddingHorizontal: 32,
      gap: 32,
    },
    $gteDesktop: {
      gap: 40,
    },
  },
  aboveDividerContentArea: {
    $base: {
      width: '100%',
      justifyContent: 'space-between',
      gap: 32,
    },
    $gteDesktop: {
      flexDirection: 'row',
    },
  },
  linkGroupArea: {
    $base: {
      flexGrow: 1,
      gap: 32,
    },
    $gteDesktop: {
      flexShrink: 1,
      flexDirection: 'row',
    },
  },
  linkGroup: {
    $base: {
      flexGrow: 1,
      gap: 8,
    },
    $gteDesktop: {
      flexShrink: 1,
    },
  },
  socialLinkArea: {
    $base: {
      alignSelf: 'flex-start',
      flexShrink: 9999,
      flexGrow: 0,
      gap: 32,
      flexWrap: 'wrap',
    },
    $gteDesktop: {
      justifyContent: 'flex-end',
    },
  },

  belowDividerContentArea: {
    $base: {
      width: '100%',
      maxWidth: '100%',
      justifyContent: 'space-between',
      gap: 32,
    },
    $gteDesktop: {
      flexDirection: 'row',
    },
  },
  legalText: {
    $base: {
      color: tokens.colors.$blackOpacity75,
      maxWidth: '100%',
    },
  },
  legalTextLink: {
    $base: {
      flexShrink: 1,
      flexWrap: 'wrap',
      color: tokens.colors.$blackOpacity75,
      textDecorationLine: 'underline',
      textDecorationColor: tokens.colors.$blackOpacity55,
    },
  },
  legalLinksArea: {
    $base: {
      gap: 16,
      flexWrap: 'wrap',
    },
  },
  legalLink: {
    $base: {
      color: tokens.colors.$blackOpacity75,
    },
  },
});

function HorizontalDivider() {
  return <View style={dividerStyles.horizontalDivider} />;
}

function VerticalDivider() {
  return <View style={dividerStyles.verticalDivider} />;
}

const dividerStyles = StyleSheet.create({
  horizontalDivider: {
    width: '100%',
    height: 1,
    backgroundColor: tokens.colors.$blackOpacity10,
  },
  verticalDivider: {
    width: 1,
    backgroundColor: tokens.colors.$blackOpacity10,
  },
});

function getIconLabel(icon: PageBuilderIcon): string {
  switch (icon) {
    case 'facebook':
      return 'Follow us on Facebook';
    case 'instagram':
      return 'Follow us on Instagram';
    case 'tik-tok':
      return 'Follow us on TikTok';
    case 'you-tube':
      return 'Follow us on YouTube';
    case 'twitter':
      return 'Follow us on Twitter';
    case 'accessible':
      return 'Accessibility';
    default:
      return icon
        .split('-')
        .map(part => (part ? part[0].toUpperCase() + part.slice(1) : part))
        .join(' ');
  }
}
